import { useMemo } from 'react';
import { ApolloClient, ApolloLink, InMemoryCache, HttpLink } from '@apollo/client';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { onError } from '@apollo/client/link/error';
import { defaultDataIdFromObject } from '@apollo/client/cache';

import { isBrowser } from 'utils/misc-utils';
import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';
import { OUTLETS_RELEASE } from 'shared/constants/api-versions';

import introspectionQueryResultData from 'shared/schemas/fragment-types';
import PublicEnv from 'shared/utils/public-env';
import { LD_SESSION_REPLAY_HEADER, getLDSessionReplayUrl } from 'shared/observability/ld-observability';

import { sha256 as jsSha256 } from 'js-sha256';

import { apolloSessionLink } from 'utils/session';
import { setCheckoutViewerSecret } from 'checkout/isolated/checkout-viewer-secret';
import { networkStatusLink } from './network-status';
import { automaticAiqOneShotLink } from './automatic-aiq-one-shot-link';
import { cellRoutedFetch, setCellContext } from './cell-routing';

// SHA-256 hash for Apollo persisted queries
async function sha256(message) {
  // Node SSR
  if (typeof window === 'undefined') {
    const { createHash } = await import('crypto');

    return createHash('sha256').update(message).digest('hex');
  }
  // Secure browser context (HTTPS or localhost)
  if (crypto?.subtle) {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Insecure HTTP context (HTTP)
  return jsSha256(message);
}

const possibleTypes = {};

introspectionQueryResultData.__schema.types.forEach((supertype) => {
  possibleTypes[supertype.name] = supertype.possibleTypes.map((t) => t.name);
});

let globalApolloClient = null;

let { consumerUrl } = PublicEnv;

if (isBrowser()) {
  consumerUrl = ``;
} else if (!consumerUrl) {
  consumerUrl = `http://localhost:${process.env.PORT || 3000}`;
}

export const EXTENSION_ERRORS = {
  AUTHERROR: 'AUTHENTICATION_ERROR',
  SESSIONEXPIRED: 'SESSION_INVALID',
};

const handleSessionExpired = (error) => {
  if (isBrowser()) {
    console.error(error);
    // "Simply remove token when session expires. Redirects should be handled at the page level."
    setPersistedValue('access-token', false);
  }
};

const errorLinkFactory = ({ onSessionExpired, onAuthError } = {}) =>
  onError(({ graphQLErrors, networkError, _operation }) => {
    // console.error(`Error in the following operation: ${operation}`);

    if (graphQLErrors) {
      graphQLErrors.forEach((error) => {
        console.error(error);
        if (error.extensions?.code === EXTENSION_ERRORS.AUTHERROR) {
          onAuthError.call(this, error);
        }
        if (error.extensions && error.extensions.code === EXTENSION_ERRORS.SESSIONEXPIRED) {
          // console.error('Session expired');
          if (onSessionExpired) {
            onSessionExpired.call(this, error);
          }
        }
      });
    }

    if (networkError) {
      console.error(`Network Error: ${networkError.message} (${networkError.statusCode})`);
    }
  });

export function setHeaders(operation, forward) {
  // API version indicates which features this client understands.
  // Used for backward-compatible feature rollouts (e.g., outlet collections).
  // Sent for both SSR and client-side requests to ensure consistent behavior.
  // @see shared/constants/api-versions.ts for available version constants.
  const headers = {
    'x-api-version': OUTLETS_RELEASE,
  };

  if (isBrowser()) {
    const token = getPersistedValue('access-token');
    headers.url = window.location.href;

    if (token) {
      headers.authorization = token;
    }

    const ldSessionReplayUrl = getLDSessionReplayUrl();
    if (ldSessionReplayUrl) {
      headers[LD_SESSION_REPLAY_HEADER] = ldSessionReplayUrl;
    }
  }

  operation.setContext(() => ({
    headers: { ...operation.getContext().headers, ...headers },
  }));

  return forward(operation);
}

export function dataIdFromObject(object) {
  if (object.__typename === 'Products' && object.adTrackers) {
    return null; // Don't normalize sponsored products
  }
  if (object.__typename === 'ProductCardSpecial') {
    return null; // Don't normalize product-scoped special descriptors
  }
  return defaultDataIdFromObject(object);
}

/**
 * Creates and configures the ApolloClient
 */
export function createApolloClient() {
  const name = `Marketplace (${PublicEnv.appEnv})`;

  const errorLink = errorLinkFactory({ onSessionExpired: handleSessionExpired, onAuthError: handleSessionExpired });

  const httpLink = new HttpLink({
    credentials: 'include',
    fetch: cellRoutedFetch,
    // Keep SSR on the same proven Express proxy path as the browser. The proxy selects
    // URL_API_INTERNAL when available and falls back to API_URL; calling PublicEnv.graphqlUri
    // directly caused SSR failures in environments where the public API is unavailable or unset.
    uri: `${consumerUrl}/graphql`,
    useGETForHashedQueries: true,
  });

  return new ApolloClient({
    connectToDevTools: isBrowser(),
    ssrMode: !isBrowser(),
    name,
    version: PublicEnv.currentVersion,
    assumeImmutableResults: true,
    cache: new InMemoryCache({
      dataIdFromObject,
      possibleTypes,
      typePolicies: {
        ProductCardSpecial: {
          keyFields: false,
        },
        // DutchiePosLoyaltyConfig has no id — merge so the dedicated points-boost query
        // document merges into the cached config instead of replacing the shared loyalty fields.
        DutchiePosLoyaltyConfig: {
          merge: true,
        },
      },
    }),
    link: ApolloLink.from([
      setHeaders,
      setCheckoutViewerSecret,
      apolloSessionLink,
      automaticAiqOneShotLink,
      errorLink,
      createPersistedQueryLink({ sha256, useGETForHashedQueries: true }),
      setCellContext,
      networkStatusLink,
      httpLink,
    ]),
  });
}

export function initializeApollo(initialState = null) {
  const isServer = typeof window === 'undefined';
  const apolloClient = globalApolloClient || createApolloClient();

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    if (isServer || !globalApolloClient) {
      apolloClient.cache.restore(initialState);
    } else {
      // Get existing cache, loaded during client side data fetching
      const existingCache = apolloClient.extract();
      // Restore the cache using the data passed from getStaticProps/getServerSideProps
      // combined with the existing cached data
      apolloClient.cache.restore({ ...existingCache, ...initialState });
    }
  }
  // For SSG and SSR always create a new Apollo Client
  if (isServer) {
    return apolloClient;
  }
  // Create the Apollo Client once in the client
  if (!globalApolloClient) {
    globalApolloClient = apolloClient;
  }
  return apolloClient;
}

export function useApollo(initialState) {
  const client = useMemo(() => initializeApollo(initialState), [initialState]);
  return client;
}
